import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, Eye, EyeOff, ArrowRight, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { login, googleLogin } from '../../services/auth';
import { AuthLayout } from '../../components/auth-layout';
import ghostIcon from '../../assets/ghost.png';

const INPUT_CLASS =
  'h-11 w-full rounded-xl px-4 text-sm outline-none transition-all duration-200 placeholder:text-white/20 focus:ring-2 focus:ring-[#AD34FE]/40';
const INPUT_STYLE = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  color: '#f5f0fa',
};
const CARD_STYLE = {
  background: 'rgba(26, 14, 36, 0.7)',
  border: '1px solid rgba(173, 52, 254, 0.15)',
  backdropFilter: 'blur(32px)',
  boxShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset',
};
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_ID;

export default function Login() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [gsiReady, setGsiReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token?.length > 1) navigate('/');
  }, []);

  useEffect(() => {
    if (window.google?.accounts?.oauth2) {
      setGsiReady(true);
      return;
    }
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener('load', () => setGsiReady(true));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => setGsiReady(true);
    document.head.appendChild(script);
  }, []);

  async function processGoogleToken(accessToken) {
    setIsLoading(true);
    try {
      const loginRes = await googleLogin(accessToken);
      localStorage.setItem('token', loginRes.data.token);
      localStorage.setItem('user', JSON.stringify(loginRes.data.user));
      toast.success('Login realizado com sucesso!');
      navigate(search.get('redirect') ?? '/');
    } catch (err) {
      if (err?.status === 404) {
        toast.error('Conta não encontrada. Solicite um agente para começar.');
        navigate('/solicitar-agente');
      } else {
        toast.error('Falha no login com Google');
      }
    } finally {
      setIsLoading(false);
    }
  }

  function handleGoogleAuth() {
    if (!GOOGLE_CLIENT_ID) {
      toast.error('Google Auth não configurado (VITE_GOOGLE_ID ausente). Reinicie o dev server.');
      return;
    }
    if (!gsiReady || !window.google?.accounts?.oauth2) {
      toast.error('Aguarde o Google carregar e tente novamente.');
      return;
    }

    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'openid email profile',
      prompt: 'select_account',
      callback: (response) => {
        if (response.error || !response.access_token) {
          toast.error('Falha no login com Google');
          return;
        }
        processGoogleToken(response.access_token);
      },
    });

    client.requestAccessToken();
  }

  function onSubmit(e) {
    e.preventDefault();
    const { email, password } = Object.fromEntries(new FormData(e.target));
    setIsLoading(true);
    login(email, password)
      .then(({ data }) => {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        toast.success('Login realizado com sucesso!');
        navigate(search.get('redirect') ?? '/');
      })
      .catch(() => {
        toast.error('Falha ao realizar login');
      })
      .finally(() => setIsLoading(false));
  }

  return (
    <AuthLayout>
      <div className="flex flex-col gap-8">
        {/* Brand */}
        <div className="flex flex-col items-center gap-5">
          <div className="animate-ghost-float relative">
            <div
              className="animate-ghost-glow absolute inset-0 rounded-full blur-2xl"
              style={{ background: '#AD34FE' }}
            />
            <img
              src={ghostIcon}
              alt="Verboo"
              className="relative h-[72px] w-[72px] object-contain drop-shadow-lg"
            />
          </div>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.35)' }}>
            Bem-vindo de volta
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-6 flex flex-col gap-5" style={CARD_STYLE}>
          <form onSubmit={onSubmit} className="flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="email"
                className="text-xs font-medium uppercase tracking-wider"
                style={{ color: 'rgba(255,255,255,0.4)' }}
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="seu@email.com"
                required
                autoComplete="email"
                className={INPUT_CLASS}
                style={INPUT_STYLE}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="text-xs font-medium uppercase tracking-wider"
                  style={{ color: 'rgba(255,255,255,0.4)' }}
                >
                  Senha
                </label>
                <Link
                  to="/forgot/password"
                  className="text-xs transition-colors hover:brightness-125"
                  style={{ color: '#AD34FE' }}
                >
                  Esqueceu a senha?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className={`${INPUT_CLASS} pr-11`}
                  style={INPUT_STYLE}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center transition-colors"
                  style={{ color: 'rgba(255,255,255,0.3)', background: 'transparent', border: 'none' }}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="mt-1 h-11 w-full rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                color: '#fff',
                background: isLoading
                  ? 'rgba(173,52,254,0.4)'
                  : 'linear-gradient(135deg, #AD34FE 0%, #7b1fd4 100%)',
                boxShadow: isLoading
                  ? 'none'
                  : '0 0 32px rgba(173,52,254,0.4), 0 4px 16px rgba(0,0,0,0.3)',
                border: 'none',
              }}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <span>Entrar</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>ou</span>
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
          </div>
          <button
            type="button"
            onClick={handleGoogleAuth}
            disabled={isLoading || !gsiReady}
            className="h-11 w-full rounded-xl text-sm font-semibold flex items-center justify-center gap-2.5 transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.7)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
            }}
          >
            <svg viewBox="0 0 48 48" className="h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            <span>Continuar com Google</span>
          </button>
        </div>

        {/* Footer */}
        <div className="flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/solicitar-agente')}
            className="text-sm transition-colors"
            style={{ color: 'rgba(255,255,255,0.4)', background: 'transparent', border: 'none' }}
          >
            Ainda não é cliente?{' '}
            <span style={{ color: '#AD34FE' }}>Solicite seu agente</span>
          </button>
          <a
            href="https://verboo.ai/chat"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 hover:scale-[1.02]"
            style={{
              color: 'rgba(255,255,255,0.5)',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <MessageCircle className="h-4 w-4" />
            Ir para o Chat
          </a>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.18)' }}>
            © {new Date().getFullYear()} Verboo. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}
